import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from '../../utils/axios';
import CookieConsent from "../../components/CookieConsent/CookieConsent";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/Button/Button";

const AuthContext = createContext();

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [showLogOutModal, setShowLogOutModal] = useState(false);

  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);

  const [payoutNeedsOnboarding, setPayoutNeedsOnboarding] = useState(null);

  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Each payout load claims a token; a resolving load only applies its result
  // if its token is still current, so a late response can't overwrite state
  // that was reset by a logout or session expiry mid-fetch.
  const payoutLoadToken = useRef(0);

  useEffect(() => {
    let userData;
    try {
      userData = JSON.parse(localStorage.getItem('user'));
    } catch (err) {
      userData = null;
    }

    if (userData) {
      setUser(userData);
    } else {
      setIsAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      payoutLoadToken.current += 1;
      setUser(null);
      setProfile(null);
      setPayoutNeedsOnboarding(null);
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, []);

    useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      if (user.isEmailVerified === false) {
        // /profile (and the /payout/details call fetchProfile triggers for
        // hosts) 403s for an unverified user (server-side gate, PP-637) — skip
        // the call entirely rather than let it fail on every load. fetchProfile
        // fires automatically once isEmailVerified flips true, since that flip
        // changes `user` and re-runs this effect.
        setIsAuthLoading(false);
      } else {
        fetchProfile();
      }
    } else {
      payoutLoadToken.current += 1;
      localStorage.removeItem('user');
      setProfile(null);
      setPayoutNeedsOnboarding(null);
    }
  }, [user]);



  const fetchProfile = async () => {
    setIsAuthLoading(true);
    try {
      // Run both calls in parallel using Promise.all
      const [profileResponse, settingsResponse] = await Promise.all([
        axios.get('/profile'),
        axios.get('/profile/settings'),
      ]);

      // Set profile data from the first response
      setProfile(profileResponse.data);

      // Get email from the settings response and set it for HubSpot
      const userEmail = settingsResponse.data?.profile?.basicDetails?.email;
      if (userEmail) {
        setHubSpotUserEmail(userEmail);
      }

      // Resolve payout status before releasing the loading gate so hosts don't
      // see the dashboard flash before the onboarding modal decision is known.
      // Payout loading owns its own error handling, so a failed payout call
      // never lands here and never sets profileError — the profile loaded fine.
      await loadPayoutStatus(profileResponse.data?.roles?.primary);
    } catch (err) {
      setProfileError(err);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const loadPayoutStatus = async (primaryRole) => {
    // Payout onboarding only applies to hosts. Non-hosts must keep the value at
    // null so the "only hosts ever leave null" invariant holds even when this
    // loader is reached through the public refresh entry point.
    if (primaryRole !== 'HOST_ADMIN') {
      payoutLoadToken.current += 1;
      setPayoutNeedsOnboarding(null);
      return;
    }
    const requestToken = (payoutLoadToken.current += 1);
    try {
      const response = await axios.get('/payout/details');
      if (requestToken !== payoutLoadToken.current) return;
      setPayoutNeedsOnboarding(!!response.data?.stripeConnectedAccountDetails?.needsOnboarding);
    } catch (err) {
      if (requestToken !== payoutLoadToken.current) return;
      // Leave the status unknown rather than committing to a decided value: the
      // gate triggers only on a strict true, so null keeps the host in the app
      // while signalling that a later refresh should re-evaluate.
      console.error(err);
      setPayoutNeedsOnboarding(null);
    }
  };

  const refreshPayoutStatus = async () => {
    await loadPayoutStatus(profile?.roles?.primary);
  };

  const logOut = () => {
    setShowLogOutModal(true);
  };

  const clearSession = async () => {
    try {
      setIsLoggingOut(true);
      await axios.get('/auth/logout');
      setShowLogOutModal(false);
      setUser(null);
      clearToken();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const LogOutModalComponent = () => {
    if (!showLogOutModal) return null;

    const closeLogOutModal = () => {
      setShowLogOutModal(false);
    };

    return (
      <Modal show={true} allowCloseVeilClick={true} handleClose={closeLogOutModal} size="small">
        <h3 className="text-2xl leading-6 font-bold text-gray-900 text-left">
          Log out
        </h3>
        <div className="text-left my-8 text-base text-gray-500">
          <p>Are you sure you want to log out?</p>
        </div>
        <div className="flex flex-row-reverse gap-x-4">
          <Button onClick={clearSession} variant="primary" size="small" loading={isLoggingOut}>
            Log Out
          </Button>
          <Button onClick={closeLogOutModal} variant="tertiary" size="small">
            Cancel
          </Button>
        </div>
      </Modal>
    );
  };

  const setToken = (token) => {
    localStorage.setItem('accesstoken', token);
  };

  const clearToken = () => {
    localStorage.removeItem('accesstoken');
  };

  const hasRole = (roles) => {
    if (!profile || !profile.roles || !profile.roles.all) {
      return false;
    }
    
    // If roles is a string, convert to array
    const rolesToCheck = Array.isArray(roles) ? roles : [roles];
    
    // Check if any of the user's roles match any of the required roles
    return rolesToCheck.some(role => profile.roles.all.includes(role));
  };

  const refreshProfile = async () => {
    setIsAuthLoading(true);
    try {
      const response = await axios.get('/profile');
      setProfile(response.data);
    } catch (err) {
      setProfileError(err);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const setHubSpotUserEmail = (userEmail) => {
    if (!window.hsConversationsSettings) {
      window.hsConversationsSettings = {};
    }
    window.hsConversationsSettings.identificationEmail = userEmail;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthLoading,
        setToken,
        logOut,
        clearSession,
        isLoggingOut,
        LogOutModalComponent,
        profile,
        profileError,
        refreshProfile,
        hasRole,
        payoutNeedsOnboarding,
        refreshPayoutStatus,
      }}
    >
      {children}
      <CookieConsent />
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
